import { Component, Input, OnChanges } from '@angular/core';

export interface SqlChartBar {
  label: string;
  value: number;
  color: string;
  widthPct: number;
}

@Component({
  selector: 'app-sql-result-chart',
  template: `
    <div class="css-chart" *ngIf="bars.length > 0">
      <div *ngFor="let bar of bars" class="css-bar-row">
        <div class="css-bar-label" [title]="bar.label">{{ bar.label }}</div>
        <div class="css-bar-track">
          <div
            class="css-bar-fill"
            [style.width.%]="bar.widthPct"
            [style.background-color]="bar.color">
          </div>
        </div>
        <div class="css-bar-value">{{ bar.value }}</div>
      </div>
    </div>
    <div *ngIf="bars.length === 0" class="css-chart-empty">No chart data available</div>
  `,
  styles: [`
    .css-chart {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 4px 0;
      min-height: 120px;
    }

    .css-bar-row {
      display: grid;
      grid-template-columns: minmax(80px, 28%) 1fr 40px;
      align-items: center;
      gap: 10px;
    }

    .css-bar-label {
      font-size: 0.75rem;
      color: #334155;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .css-bar-track {
      height: 22px;
      background: #f1f5f9;
      border-radius: 6px;
      overflow: hidden;
    }

    .css-bar-fill {
      height: 100%;
      min-width: 4px;
      border-radius: 6px;
      transition: width 0.4s ease;
    }

    .css-bar-value {
      font-size: 0.78rem;
      font-weight: 600;
      color: #1e293b;
      text-align: right;
    }

    .css-chart-empty {
      padding: 24px;
      text-align: center;
      color: #94a3b8;
      font-size: 0.85rem;
    }
  `],
})
export class SqlResultChartComponent implements OnChanges {
  @Input() labels: string[] = [];
  @Input() values: number[] = [];
  @Input() colors: string[] = [
    '#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706',
    '#dc2626', '#db2777', '#4f46e5', '#0d9488', '#ca8a04',
  ];

  bars: SqlChartBar[] = [];

  ngOnChanges(): void {
    this.bars = this.buildBars();
  }

  private buildBars(): SqlChartBar[] {
    if (!this.labels?.length || !this.values?.length) {
      return [];
    }

    const pairs = this.labels
      .map((label, i) => ({ label: String(label), value: Number(this.values[i] || 0) }))
      .filter((item) => item.label && !isNaN(item.value));

    if (!pairs.length) {
      return [];
    }

    const max = Math.max(...pairs.map((p) => p.value), 1);
    return pairs.map((item, i) => ({
      label: item.label,
      value: item.value,
      color: this.colors[i % this.colors.length],
      widthPct: Math.max(4, Math.round((item.value / max) * 100)),
    }));
  }
}
